import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AdminPortalAPIHttpClient } from '../http/v2/AdminPortalAPIHttpClient';
import { UserContextProvider } from '../context/user-context-provider';
import { waitForAccount } from './role-context';
import { ALL_MODULES, PermissionMatchMode, UserPermissionsResponse } from './UserPermissions';
interface PermissionSnapshot {
  readonly username: string | undefined;
  readonly isSuperAdmin: boolean;
  readonly moduleIds: readonly string[];
  readonly hasModule: (moduleId: string) => boolean;
  readonly has: (permission: string) => boolean;
}

const DENY_ALL: PermissionSnapshot = Object.freeze({
  username: undefined,
  isSuperAdmin: false,
  moduleIds: Object.freeze([]),
  hasModule: () => false,
  has: () => false
});

const normalize = (value: string): string => value.trim().toLowerCase();

@Injectable({
  providedIn: 'root'
})
export class OperationAccessService {

  #snapshot: Promise<PermissionSnapshot> | undefined;

  constructor(
    private adminPortalHttpClient: AdminPortalAPIHttpClient,
    private userContextProvider: UserContextProvider
  ) {
    // A different user signing in within the same app instance must not reuse the old snapshot.
    this.userContextProvider.account$.subscribe(account => {
      const username = account?.username;
      this.#snapshot?.then(snapshot => {
        if (snapshot.username !== username) {
          this.#snapshot = undefined;
        }
      });
    });
  }

  async hasPermission(permissions: string | readonly string[], mode: PermissionMatchMode = 'any'): Promise<boolean> {
    const required = (Array.isArray(permissions) ? permissions : [permissions]).filter(Boolean);

    if (required.length === 0) {
      return true;
    }

    const snapshot = await this.getSnapshot();

    if (snapshot.isSuperAdmin) {
      return true;
    }

    return mode === 'all'
      ? required.every(permission => snapshot.has(permission))
      : required.some(permission => snapshot.has(permission));
  }

  async hasModule(moduleId: string): Promise<boolean> {
    const snapshot = await this.getSnapshot();
    return snapshot.isSuperAdmin || snapshot.hasModule(moduleId);
  }

  async isSuperAdmin(): Promise<boolean> {
    const snapshot = await this.getSnapshot();
    return snapshot.isSuperAdmin;
  }

  async getAllowedModuleIds(): Promise<string[] | typeof ALL_MODULES> {
    const snapshot = await this.getSnapshot();
    return snapshot.isSuperAdmin ? ALL_MODULES : [...snapshot.moduleIds];
  }

  private getSnapshot(): Promise<PermissionSnapshot> {
    if (!this.#snapshot) {
      this.#snapshot = this.loadSnapshot();
    }

    return this.#snapshot;
  }

  private async loadSnapshot(): Promise<PermissionSnapshot> {
    try {
      await waitForAccount(this.userContextProvider);

      const response = await firstValueFrom(
        this.adminPortalHttpClient.get('/me/permissions')
      ) as UserPermissionsResponse;

      const modules = new Set((response?.moduleIds ?? []).map(normalize));
      const granted = new Set((response?.permissions ?? []).map(normalize));

      return Object.freeze({
        username: this.userContextProvider.account?.username,
        // isSuperAdmin: true,
        isSuperAdmin: response?.isSuperAdmin === true,
        moduleIds: Object.freeze([...modules]),
        hasModule: (moduleId: string) => modules.has(normalize(moduleId)),
        has: (permission: string) => granted.has(normalize(permission))
      });
    } catch (e) {
      console.error('Error while retrieving user permissions:', e);
      this.#snapshot = undefined;
      return DENY_ALL;
    }
  }
}
